import {
  Button,
  Divider,
  Group,
  Stack,
  Flex,
  Text,
  Title,
  Tooltip,
  Paper,
  Space,
} from '@mantine/core';
import {
  useInViewport,
  useMergedRef,
  useScrollIntoView,
} from '@mantine/hooks';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {
  createFileRoute,
  Link,
  useNavigate,
} from '@tanstack/react-router';
import React, {
  type RefObject,
  useRef,
} from 'react';

import {
  type DigitalWorkerApiResponse,
  type DigitalWorkerWithId,
} from '#features/digital-workers/DigitalWorkers.schema';
import {
  UserDigitalWorkers,
} from '#features/digital-workers/UserDigitalWorkers';
import {
  easeOutElastic,
} from '#utils/Easings';

export const Route = createFileRoute('/_app/_authenticated/mydigitalworkers/')({
  loader: async (opts) => {
    const workers = opts.context.queryClient.ensureQueryData({
      queryKey: ['digital-workers'],
    });
    return workers;
  },
  component: Dashboard,
});


function Dashboard(): React.ReactNode {
  const workersResponse = Route.useLoaderData() as DigitalWorkerApiResponse;
  const workersList = workersResponse.data;
  const navigate = useNavigate();
  const digitalWorkersRef = useRef<HTMLElement>();
  const {
    ref: digitalWorkersInViewportRef,
    inViewport: digitalWorkersInViewport,
  } = useInViewport();
  const {
    scrollIntoView: scrollToDigitalWorkers,
    targetRef: digitalWorkersScrollRef,
  } = useScrollIntoView<HTMLElement>({
    axis: 'y',
    offset: 60,
    isList: true,
    duration: 750,
    easing: easeOutElastic,
  });
  const digitalWorkersMergedRef = useMergedRef(
    digitalWorkersRef,
    digitalWorkersInViewportRef,
    digitalWorkersScrollRef,
  );
  return (
    <Flex
      w="100%"
      h="100%"
      direction="column"
    >
      <div
        className="home-page"
        style={ {
          width: '100%',
          display: 'flex',
          textAlign: 'left',
          justifyContent: 'center',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'column',
        } }
      >
        <Stack
          pl="4vw"
          w="100%"
        >
          <Text
            fw={ 200 }
            fz="6.5vw"
            variant="gradient"
            gradient={ {
              from: 'oklch(0.43 0 0)',
              to: 'oklch(0.83 0 0)',
              deg: 5,
            } }
            style={ {
              letterSpacing: '0.8vw',
            } }
          >
            SPECIA
          </Text>
          <Group>
            <Divider
              orientation="vertical"
              color="transparent.9"
              size="lg"
              mih="100%"
              pr="0px"
            />
            <Text
              fw="300"
              w="30%"
              c="transparent.9"
              size="md"
              pl="xs"
            >
              Digital workers with advanced intelligence streamline industries,
              <br />
              managing repetitive tasks, interacting with humans and providing analytics.
              <br />
              <br />
              Unlike humans, they operate error-free and continuously.
            </Text>
          </Group>
          { !digitalWorkersInViewport && (
            <div
              style={ {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: '2.5vw',
              } }
            >
              <ArrowDownwardOutlinedIcon
                onClick={ () => {
                  scrollToDigitalWorkers({
                    alignment: 'center',
                  });
                } }
                style={ {
                  fontSize: '0.8vw',
                  border: '1.5px solid oklch(1 0 0 / 0.6)',
                  borderRadius: '10vw',
                  height: '2.8vw',
                  width: '2.8vw',
                  padding: '0.5vw',
                  cursor: 'pointer',
                  color: 'oklch(1 0 0 / 0.7)',
                } }
              />
              <div
                style={ {
                  marginLeft: '1.25vw',
                  color: 'oklch(1 0 0 / 0.6)',
                  fontSize: '0.9vw',
                } }
              >
                My Digital Workers
              </div>
            </div>
          ) }
        </Stack>
      </div>
      <Space h="xl" />
      <div
        className="quick-access"
        style={ {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        } }
      >
        <Paper
          shadow="xl"
          radius="xl"
          mt="xl"
          p="sm"
          bg="oklch(0.64 0 0 / 0.15)"
          withBorder
        >
          <Stack
            align="stretch"
            justify="flex-start"
            gap="md"
            px="xl"
          >
            <Title
              order={ 3 }
              ta="start"
              pt="xl"
            >
              Meet Your Digital Workers
            </Title>
            <Text
              ta="start"
              size="xl"
            >
              Boost your productivity
            </Text>
            { workersList.length > 0 ? (
              <UserDigitalWorkers
                scrollRef={ digitalWorkersMergedRef as unknown as RefObject<HTMLDivElement> }
                workersResponse={ workersResponse }
              />
            ) : (
              <div
                style={ {
                  gridColumn: window.innerWidth <= 768 ? 'span 2' : 'repeat(5, 1fr)',
                  textAlign: 'center',
                  marginTop: '10px',
                } }
              >
                <Button
                  component={ Link }
                  to="/store"
                  w="100%"
                  h={ window.innerWidth <= 768 ? '30vw' : '8vw' }
                  c="transparent.9"
                  bg="transparent.2"
                  fz="window.innerWidth <= 768 ? '3vw' : '1vw'"
                  style={ {
                    borderRadius: window.innerWidth <= 768 ? '1vw' : '5px',
                    border: '1px solid var(--mantine-color-transparent-2))',
                  } }
                >
                  Go to Marketplace
                </Button>
              </div>
            ) }
          </Stack>
        </Paper>
      </div>
    </Flex>
  );
}
